import React, { useEffect, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { VISIT_STATUS, VISIT_TYPES, formatVisitId } from '../../utils/constants';
import './VisitCalendar.css';

const VisitCalendar = ({ visits, activeStage, filters, onEventClick, onDateClick, selectedVisit, plannerData }) => {
  const calendarRef = useRef(null);

  const getVisitCraFilterId = (visit) => {
    if (!visit) return '';
    const craId = visit.cra_UserID ?? visit.CRA_UserID ?? visit.craID ?? visit.Cra_UserID ?? visit.CraUserID;
    return craId != null && String(craId).trim() !== '' ? String(craId) : '';
  };

  // Build events from Visit Scheduling Planner data (saved SMV + suggested SMV only; SIVs are not shown)
  const getPlannerEvents = () => {
    if (!plannerData) return [];
    const events = [];
    const siteFilter = (filters && filters.site) ? filters.site : 'all';
    const matchSite = (siteName, siteCode) => {
      if (siteFilter === 'all') return true;
      const name = (siteName || '').toString().trim();
      const code = (siteCode || '').toString().trim();
      return siteFilter === name || siteFilter === code;
    };
    const toDateStr = (d) => {
      if (!d) return undefined;
      const x = typeof d === 'string' ? d.split('T')[0] : d;
      return typeof x === 'string' ? x : undefined;
    };
    const addEnd = (startStr) => {
      if (!startStr) return null;
      const end = new Date(startStr + 'T00:00:00Z');
      end.setUTCDate(end.getUTCDate() + 1);
      return end.toISOString().split('T')[0];
    };

    const savedSmv = (plannerData.savedSmvVisits || []).filter((v) =>
      matchSite(v.siteName || v.SiteName || v.siteCode, v.siteCode || v.SiteCode || v.siteName || v.SiteName)
    );
    savedSmv.forEach((v) => {
      const startStr = toDateStr(v.plannedStartDate);
      if (!startStr) return;
      const title = `${formatVisitId(v)} - ${v.siteCode || v.siteName || ''}`;
      events.push({
        id: `smv-${v.scheduledVisitID}`,
        title,
        start: startStr,
        end: addEnd(toDateStr(v.plannedEndDate)) || addEnd(startStr),
        allDay: true,
        backgroundColor: '#2180a1',
        borderColor: '#1a6480',
        textColor: '#ffffff',
        extendedProps: { visit: v, isSuggested: false, isPlanner: true },
      });
    });

    const sitesWithSuggestions = (plannerData.sitesWithSuggestions || []).filter((site) =>
      matchSite(site.siteName || site.SiteName, site.siteCode || site.SiteCode)
    );
    sitesWithSuggestions.forEach((site) => {
      (site.suggestedDates || []).forEach((dateStr, idx) => {
        const startStr = typeof dateStr === 'string' ? dateStr.split('T')[0] : dateStr;
        if (!startStr) return;
        const title = `Suggested SMV - ${site.siteCode || site.siteName || ''}`;
        events.push({
          id: `suggested-${site.siteId}-${startStr}-${idx}`,
          title,
          start: startStr,
          end: addEnd(startStr),
          allDay: true,
          backgroundColor: '#fef3c7',
          borderColor: '#f59e0b',
          textColor: '#92400e',
          className: 'fc-event-suggested',
          extendedProps: {
            isSuggested: true,
            isPlanner: true,
            suggestedDate: startStr,
            siteId: site.siteId,
            siteName: site.siteName,
            siteCode: site.siteCode,
            projectCode: site.projectCode,
            studyId: site.studyId,
            piName: site.pIName,
            frequency: site.frequency,
            nSiteVisits: site.nSiteVisits,
          },
        });
      });
    });

    return events;
  };

  // Convert visits to calendar events
  const getCalendarEvents = () => {
    if (plannerData) return getPlannerEvents();
    return visits
      .filter(v => {
        // Filter by site (filter value is siteCode; align with VisitManagement)
        if (filters.site !== 'all' && (v.siteCode ?? v.SiteCode) !== filters.site) return false;
        // Filter by CRA user ID so the selected CRA matches ScheduledVisit.CRA_UserID
        if (filters.cra !== 'all' && getVisitCraFilterId(v) !== filters.cra) return false;
        return true;
      })
      .filter(v => {
        const getStatus = (visit) => visit.visitStatus ?? visit.VisitStatus;
        const getCrfStage = (visit) => visit.crfStage ?? visit.CrfStage ?? '';
        const status = getStatus(v);
        const crfStage = getCrfStage(v);
        // Filter by stage (align with VisitManagement getVisitsByStage)
        switch (activeStage) {
          case 'schedule':
            return status === VISIT_STATUS.DRAFT || status === VISIT_STATUS.PLANNED || status === VISIT_STATUS.CONFIRMED;
          case 'onGoing':
            return status === VISIT_STATUS.ONGOING && crfStage !== 'First Review' && crfStage !== 'Second Review';
          case 'pmReview':
            return status === 'PM_REVIEW' || crfStage === 'First Review';
          case 'srmReview':
            return status === 'SRM_REVIEW' || crfStage === 'Second Review';
          case 'closed':
            return status === VISIT_STATUS.COMPLETED;
          default:
            return true;
        }
      })
      .map(visit => {
        if (!visit) return null;

        // Backend sends full datetime (e.g. '2026-01-15T00:00:00'),
        // but for all-day events we only care about the calendar date.
        // Avoid converting to UTC via toISOString(), which can shift the day.
        const startDateStr = visit.plannedStartDate
          ? visit.plannedStartDate.split('T')[0]
          : undefined;

        // For all-day events, FullCalendar treats "end" as exclusive.
        // To display a visit from PlannedStartDate..PlannedEndDate inclusively
        // (e.g., 22–24 Jan), we must pass end = PlannedEndDate + 1 day.
        let endDateStr = undefined;
        if (visit.plannedEndDate) {
          const rawEndDateStr = visit.plannedEndDate.split('T')[0];
          // Work in UTC to avoid timezone shifts when using toISOString
          const endUtc = new Date(rawEndDateStr + 'T00:00:00Z');
          endUtc.setUTCDate(endUtc.getUTCDate() + 1);
          endDateStr = endUtc.toISOString().split('T')[0];
        }
        
        // Determine event color based on status
        let backgroundColor = '#2180a1'; // default primary
        let borderColor = '#1a6480';
        
        switch (visit.visitStatus) {
          case VISIT_STATUS.COMPLETED:
            backgroundColor = '#20a084'; // success
            borderColor = '#1a8070';
            break;
          case VISIT_STATUS.ONGOING:
            backgroundColor = '#3b82f6'; // info
            borderColor = '#2563eb';
            break;
          case VISIT_STATUS.CONFIRMED:
            backgroundColor = '#f59e0b'; // warning
            borderColor = '#d97706';
            break;
          case 'PM_REVIEW':
          case 'SRM_REVIEW':
            backgroundColor = '#a84b2f'; // warning
            borderColor = '#8b3d26';
            break;
          default:
            backgroundColor = '#2180a1';
            borderColor = '#1a6480';
        }

        const siteCode = visit.siteCode || visit.siteName || '';
        const title = `${formatVisitId(visit)} - ${siteCode}`.trim().replace(/\s*-\s*$/, '') || 'Visit';

        return {
          id: visit.scheduledVisitID?.toString() || '',
          title,
          start: startDateStr,
          end: endDateStr || null,
          allDay: true,
          backgroundColor: backgroundColor,
          borderColor: borderColor,
          textColor: '#ffffff',
          extendedProps: {
            visit: visit,
            stage: activeStage,
            site: visit.siteName || '',
            cra: visit.craName || '',
            visitType: VISIT_TYPES[visit.visitType] || visit.visitType || '',
            status: visit.visitStatus || '',
          },
        };
      })
      .filter(event => event !== null);
  };

  const handleEventClick = (clickInfo) => {
    if (!onEventClick) return;
    const props = clickInfo.event.extendedProps;
    if (props.isSuggested) {
      onEventClick(props);
    } else {
      onEventClick(props.visit);
    }
  };

  const handleDateClick = (dateClickInfo) => {
    // Don't trigger if clicking on an event
    if (dateClickInfo.jsEvent.target.closest('.fc-event')) {
      return;
    }
    
    if (onDateClick) {
      onDateClick(dateClickInfo.date);
    }
  };

  // Update calendar when visits, filters, or stage change
  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.removeAllEvents();
      calendarApi.addEventSource(getCalendarEvents());
    }
  }, [visits, activeStage, filters, plannerData]);

  return (
    <div className="visit-calendar-container">
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay'
        }}
        height="auto"
        events={getCalendarEvents()}
        eventClick={handleEventClick}
        dateClick={handleDateClick}
        editable={false}
        dayMaxEvents={4}
        moreLinkClick="popover"
        eventDisplay="block"
        eventTimeFormat={{
          hour: '2-digit',
          minute: '2-digit',
          meridiem: false
        }}
        slotMinTime="06:00:00"
        slotMaxTime="22:00:00"
        weekends={true}
        firstDay={1}
        locale="en"
        buttonText={{
          today: 'Today',
          month: 'Month',
          week: 'Week',
          day: 'Day'
        }}
      />
    </div>
  );
};

export default VisitCalendar;
